import { Vector3, type Fog, type Group, type PerspectiveCamera, type WebGLRenderer } from 'three';
import type { Conditions } from '@accra/shared';
import type { BusState, RunState } from '../../game/RunState';
import { setLampGlow } from '../../world/props/streetProps';
import { nightGlow } from '../../world/buildings/facadeAtlas';
import type { RoadTiles } from '../../world/road/roadTiles';
import type { SceneRig } from '../scene';
import { SUN, sunDirection } from '../sky';
import { GlowPoints } from './glow';
import { oppositeLaneX } from '../../world/road/roadLayout';
import { createHeadlights } from './headlights';
import { lookFor, type Look } from './looks';
import { RainField } from './rain';

const TAIL_Y = 0.85;

/**
 * Weather and time of day for the scene: sky, fog and light colours, rain, a wet road, glowing street lamps
 * and shop signs, tail lights and the trotro's headlights. `apply` switches look; `update` runs every frame.
 */
export class Atmosphere {
  private look: Look = lookFor({ weather: 'clear', time: 'day' });
  private readonly rain = new RainField();
  private readonly lamps = new GlowPoints('#ffd9a0', 7, 64);
  private readonly tails = new GlowPoints('#ff3322', 1.5, 160);
  /** The headlights of the cars coming the other way. */
  private readonly fronts = new GlowPoints('#fff1c8', 2.2, 40);
  private headlights: Group | null = null;
  private readonly tailPoints: Vector3[] = Array.from({ length: 160 }, () => new Vector3());

  constructor(private readonly rig: SceneRig, private readonly renderer: WebGLRenderer, private readonly road: RoadTiles) {
    rig.scene.add(this.rain.object, this.lamps.object, this.tails.object, this.fronts.object);
  }

  /** Hangs the headlights on the player's trotro (call whenever the model changes). */
  attachHeadlights(bus: Group, length: number, width: number): void {
    this.headlights?.removeFromParent();
    this.headlights = createHeadlights(-length / 2, width / 2);
    this.headlights.visible = this.look.headlights;
    bus.add(this.headlights);
  }

  /** Switches to the look for these conditions. */
  apply(conditions: Conditions): void {
    const look = lookFor(conditions);
    this.look = look;
    const { rig, renderer } = this;
    const uniforms = rig.sky.material.uniforms;
    uniforms.turbidity!.value = look.sky.turbidity;
    uniforms.rayleigh!.value = look.sky.rayleigh;
    uniforms.mieCoefficient!.value = look.sky.mie;
    uniforms.mieDirectionalG!.value = look.sky.mieG;
    rig.sunDirection.copy(sunDirection(look.sunElevationDeg));
    uniforms.sunPosition!.value.copy(sunDirection(Math.max(look.sunElevationDeg, -SUN.elevationDeg)));
    rig.sun.color.set(look.sunColor);
    rig.sun.intensity = look.sunIntensity;
    rig.hemi.color.set(look.hemiSky);
    rig.hemi.groundColor.set(look.hemiGround);
    rig.hemi.intensity = look.hemiIntensity;
    rig.scene.environmentIntensity = look.environmentIntensity;
    renderer.toneMappingExposure = look.exposure;
    const fog = rig.scene.fog as Fog;
    fog.color.set(look.fog.color);
    fog.near = look.fog.near;
    fog.far = look.fog.far;
    (rig.scene.background as { set(c: string): void }).set(look.fog.color);

    this.road.setWet(look.wet);
    this.rain.visible = look.rain;
    nightGlow.value = look.night;
    setLampGlow(look.night);
    this.lamps.setStrength(look.night);
    this.tails.setStrength(look.headlights ? Math.max(0.6, look.night) : 0);
    this.fronts.setStrength(look.headlights ? Math.max(0.6, look.night) : 0);
    if (this.headlights) this.headlights.visible = look.headlights;
  }

  update(dt: number, state: RunState, camera: PerspectiveCamera): void {
    if (this.look.rain) this.rain.update(dt, state.bus.speed, camera.position.x, camera.position.y, camera.position.z);
    if (this.look.night > 0.02) this.lamps.set(this.road.lampPositions());
    if (this.look.headlights) {
      this.tails.set(this.tailLights(state));
      this.fronts.set(this.oncomingLights(state));
    }
  }

  /** Two bright glows at the front of every car coming the other way. */
  private oncomingLights(state: RunState): Vector3[] {
    const lights: Vector3[] = [];
    for (const car of state.oncoming) {
      if (Math.abs(car.s - state.bus.s) > 150 || lights.length >= 38) continue;
      for (const side of [-1, 1]) lights.push(new Vector3(oppositeLaneX(car.lane) + side * car.kind.width * 0.39, TAIL_Y, -car.s - car.kind.length / 2));
    }
    return lights;
  }

  /** Two red glows at the back of every car on the road and of our own trotro. */
  private tailLights(state: RunState): Vector3[] {
    let n = 0;
    const add = (x: number, s: number, half: number, length: number): void => {
      for (const side of [-1, 1]) {
        if (n >= this.tailPoints.length) return;
        this.tailPoints[n++]!.set(x + side * half * 0.78, TAIL_Y, -s + length / 2);
      }
    };
    const bus: BusState = state.bus;
    add(bus.x, bus.s, bus.size.width / 2, bus.size.length);
    for (const car of state.traffic) if (!car.kind.hoppable && Math.abs(car.s - bus.s) < 120) add(car.x, car.s, car.kind.width / 2, car.kind.length);
    return this.tailPoints.slice(0, n);
  }
}

